import { substituteQuery as substitute } from 'shared/utils/querySubstitution';
import { areStringsInsensitivelyEqual, capitalizeString } from 'shared/utils/utils';
import { Limits, MappingSymbolsCount, executeInBatches } from 'shared/excel/runtime/flowstepExecution';
import { resolveFilterSubstitutions } from 'shared/utils/filterManager';
import { RangeAddress } from 'shared/excel/rangeAddress';


export async function substituteQuery(context, query, queryType, queryWorksheetId, limits) {
  return await substitute(query, queryType, {
    tablesLoader: async (tables) => await loadTableValues(context, tables, limits),
    rangesLoader: async (ranges) => await loadRangesValues(context, ranges, queryWorksheetId, limits),
  });
}

export async function substituteFilters(context, filters, flowstepWorksheetId, limits) {
  return await resolveFilterSubstitutions(filters, {
      tablesLoader: async (tables) => await loadTableValues(context, tables, limits),
      rangesLoader: async (ranges) => await loadRangesValues(context, ranges, flowstepWorksheetId, limits),
  });
}

const RANGE_PROPS_TO_LOAD = ['rowIndex', 'columnIndex', 'values', 'numberFormat', 'valueTypes'];
const WORKSHEET_PROPS_TO_LOAD = ['id', 'name'];

async function loadLargeRange(context, range, limits, propsToLoad) {
  range.load(['address']);
  await context.sync();

  const rangeProxy = new RangeAddress(range.address);
  let processedCells = 0;
  const worksheet = range.worksheet;

  const ranges = await executeInBatches(
    Math.max(...Object.values(MappingSymbolsCount)),
    limits,
    rangeProxy.rowCount,
    async chunkSize => {
      const rangeAddress = rangeProxy.getOffsetAndResizedRange({ rowDelta: chunkSize, rowOffset: processedCells }, { useAbsoluteDelta: true });
      processedCells += chunkSize;
      return worksheet.getRange(rangeAddress.shortAddress).load(propsToLoad);
    },
    range => [range],
    async () => await context.sync()
  );

  return ranges;
} 


export async function loadRangesValues(context, groupedRanges, queryWorksheetId, limits = Limits.OfficeWeb) {
  const wss = context.workbook.worksheets;
  const queryWs = wss.getItemOrNullObject(queryWorksheetId);

  const loadedGroupedRanges = groupedRanges.flatMap(({ worksheetName, substitutions }) => {
    const worksheet = (worksheetName ? wss.getItemOrNullObject(worksheetName) : queryWs).load(WORKSHEET_PROPS_TO_LOAD);
    const loadedRanges = substitutions.map(substitution => {
      const { name, address } = substitution.range;
      const range = name
        ? (worksheetName ? worksheet.names : context.workbook.names).getItemOrNullObject(name).getRangeOrNullObject()
        : worksheet.getRange(address.shortAddress);

      return {
        loadedRange: range,
        worksheet,
        worksheetName,
        substitution,
      };
    });

    return loadedRanges;
  });

  await context.sync();

  const buildRangeError = (range, worksheetName, loadError = false) => {
    const e = range.name ? `named range '${range.name}'` : `range with address '${range.address.shortAddress}'`
    const where = range.name && !worksheetName ? 'in the workbook' : `at the worksheet '${worksheetName}'`
    return loadError ? `Failed to load ${e} ${where}` : `${capitalizeString(e)} is not found ${where}`;
  }

  const loadedRanges = [];

  for (const x of loadedGroupedRanges) {
    const load = async () => {
      const { worksheet, worksheetName, loadedRange, substitution } = x;
      try {
        if (worksheet.isNullObject) {
          return { substitution, error: `Worksheet '${worksheetName}' doesn't exist` };
        }

        if (loadedRange.isNullObject) {
          return { substitution, error: buildRangeError(substitution.range, worksheetName) };
        }

        const ranges = await loadLargeRange(context, loadedRange, limits, RANGE_PROPS_TO_LOAD)

        return { ranges, substitution };
      }
      catch (e) {
        return { substitution, error: buildRangeError(substitution.range, worksheetName, true) };
      }
    }

    loadedRanges.push(await load());
  };

  return loadedRanges;
}

export async function loadTableValues(context, tableSubstitutions, limits = Limits.OfficeWeb) {
  const loadedTables = tableSubstitutions.map(substitution => {
    // console.log('Loading table: ', name, columnName);
    const { name, columnName } = substitution.table;
    const table = context.workbook.tables.getItemOrNullObject(name);
    const worksheet = table.worksheet.load(WORKSHEET_PROPS_TO_LOAD);
    const dataBodyRange = table.getDataBodyRange();
    const headerRowRange = table.getHeaderRowRange().load(['values']);
    return { worksheet, table: { name, columnName, headerRowRange, dataBodyRange }, substitution };
  });

  await context.sync();

  const loadedRanges = [];
  for (const loadedTable of loadedTables) {
    const load = async () => {
      const { worksheet, table, substitution } = loadedTable;
      const { columnName, name, headerRowRange, dataBodyRange } = table;
      try {
        if (headerRowRange.isNullObject) {
          return { substitution, error: `Table '${name}' wasn't found` };
        }

        const headers = headerRowRange.values[0];
        const columnOffset = headers.findIndex(x => areStringsInsensitivelyEqual(x, columnName));
        if (-1 === columnOffset) {
          return { substitution, error: `Unable to find column '${columnName}' in table '${name}'` };
        }

        const column = dataBodyRange.getColumn(columnOffset);
        const ranges = await loadLargeRange(context, column, limits, RANGE_PROPS_TO_LOAD);

        return { ranges, substitution };
      }
      catch (e) {
        return { substitution, error: `Unable to load values for column ${columnName} from table ${name}` }
      }
    };

    loadedRanges.push(await load());
  };

  return loadedRanges;
}